import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Globe,
  ListTodo,
  Megaphone,
  Mail,
  FileText,
  Search,
  Download,
} from "lucide-react";

/**
 * Campaign Tracker — unified marketing dashboard.
 *
 * Joins social_campaigns with their child posts, tasks, and seo_daily_tasks so a
 * journalist-prep weekend has a single place to see every campaign's status,
 * post counts, owners, and external links.
 */

type Campaign = {
  id: string;
  name: string;
  description: string | null;
  campaign_type: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  geo_scope: string[] | null;
  platforms: string[] | null;
  notes: string | null;
};

type PostStats = {
  campaign_id: string;
  total: number;
  pending: number;
  approved: number;
  denied: number;
  posted: number;
  scheduled: number;
  has_external_url: number;
  content_types: string[];
  platforms: string[];
};

type RecentPost = {
  id: string;
  campaign_id: string;
  platform: string;
  content_type: string | null;
  status: string;
  state_code: string | null;
  external_post_id: string | null;
  posted_at: string | null;
  scheduled_at: string | null;
};

type TaskStats = {
  campaign_id: string;
  total_tasks: number;
  open_tasks: number;
  done_tasks: number;
};

const STATUS_COLOR: Record<string, string> = {
  active: "bg-green-500/15 text-green-600 border-green-500/30",
  draft: "bg-gray-400/15 text-gray-600 border-gray-400/30",
  paused: "bg-yellow-500/15 text-yellow-700 border-yellow-500/30",
  done: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  archived: "bg-slate-300/30 text-slate-500 border-slate-300/40",
};

const TYPE_ICON: Record<string, JSX.Element> = {
  data_study: <FileText size={14} />,
  outreach: <Mail size={14} />,
  launch: <Megaphone size={14} />,
  content: <FileText size={14} />,
  seo: <Search size={14} />,
};

function externalLinkFor(platform: string, externalId: string | null): string | null {
  if (!externalId) return null;
  switch (platform.toLowerCase()) {
    case "x":
    case "twitter":
      return `https://x.com/i/status/${externalId}`;
    case "linkedin":
      return `https://www.linkedin.com/feed/update/${externalId}`;
    case "facebook":
      return `https://www.facebook.com/${externalId}`;
    case "instagram":
      return `https://www.instagram.com/p/${externalId}`;
    case "youtube":
      return `https://youtube.com/shorts/${externalId}`;
    case "tiktok":
      return `https://www.tiktok.com/@AHPTransparency/video/${externalId}`;
    case "bluesky":
      return `https://bsky.app/profile/AHPTransparency.bsky.social/post/${externalId}`;
    case "reddit":
      return `https://reddit.com/comments/${externalId}`;
    default:
      return null;
  }
}

const CampaignTracker = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [postStats, setPostStats] = useState<Record<string, PostStats>>({});
  const [taskStats, setTaskStats] = useState<Record<string, TaskStats>>({});
  const [recentPosts, setRecentPosts] = useState<Record<string, RecentPost[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);

    const { data: cs } = await supabase
      .from("social_campaigns" as any)
      .select("*")
      .order("status", { ascending: false })
      .order("name");
    setCampaigns((cs ?? []) as Campaign[]);

    const { data: posts } = await supabase
      .from("social_posts" as any)
      .select("id, campaign_id, platform, content_type, status, state_code, external_post_id, posted_at, scheduled_at")
      .order("created_at", { ascending: false });

    const stats: Record<string, PostStats> = {};
    const recent: Record<string, RecentPost[]> = {};

    (posts ?? []).forEach((p: any) => {
      if (!p.campaign_id) return;
      if (!stats[p.campaign_id]) {
        stats[p.campaign_id] = {
          campaign_id: p.campaign_id,
          total: 0, pending: 0, approved: 0, denied: 0, posted: 0, scheduled: 0,
          has_external_url: 0, content_types: [], platforms: [],
        };
      }
      const s = stats[p.campaign_id];
      s.total += 1;
      if (p.status === "pending_approval") s.pending += 1;
      if (p.status === "approved") s.approved += 1;
      if (p.status === "denied") s.denied += 1;
      if (p.posted_at) s.posted += 1;
      if (p.scheduled_at) s.scheduled += 1;
      if (p.external_post_id) s.has_external_url += 1;
      if (p.content_type && !s.content_types.includes(p.content_type)) s.content_types.push(p.content_type);
      if (p.platform && !s.platforms.includes(p.platform)) s.platforms.push(p.platform);

      if (!recent[p.campaign_id]) recent[p.campaign_id] = [];
      if (recent[p.campaign_id].length < 5) recent[p.campaign_id].push(p as RecentPost);
    });

    setPostStats(stats);
    setRecentPosts(recent);

    const { data: tasks } = await supabase
      .from("tasks" as any)
      .select("campaign_id, status")
      .not("campaign_id", "is", null);
    const taskAgg: Record<string, TaskStats> = {};
    (tasks ?? []).forEach((t: any) => {
      if (!taskAgg[t.campaign_id]) taskAgg[t.campaign_id] = { campaign_id: t.campaign_id, total_tasks: 0, open_tasks: 0, done_tasks: 0 };
      taskAgg[t.campaign_id].total_tasks += 1;
      if (t.status === "done" || t.status === "completed") taskAgg[t.campaign_id].done_tasks += 1;
      else taskAgg[t.campaign_id].open_tasks += 1;
    });
    setTaskStats(taskAgg);

    setLoading(false);
  }

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      if (filterStatus !== "all" && c.status !== filterStatus) return false;
      if (filterType !== "all" && c.campaign_type !== filterType) return false;
      return true;
    });
  }, [campaigns, filterStatus, filterType]);

  const toggle = (id: string) => {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  };

  /**
   * Download a press-kit packet for the given campaign as a single Markdown file.
   * Bundles: campaign description + journalist pitch (if exists) + data brief +
   * top hospital spotlights — formatted so it can be pasted into a reporter email
   * or attached as a press kit.
   */
  async function downloadPressKit(c: Campaign) {
    const { data: posts } = await supabase
      .from("social_posts" as any)
      .select("content_type, content, state_code, city_name, platform")
      .eq("campaign_id", c.id);
    const all = (posts ?? []) as Array<{ content_type: string | null; content: string; state_code: string | null; city_name: string | null; platform: string }>;
    const byType = (t: string) => all.filter((p) => p.content_type === t).map((p) => p.content);

    const today = new Date().toISOString().slice(0, 10);
    const md: string[] = [];
    md.push(`# Press Kit — ${c.name}`);
    md.push("");
    md.push(`*Prepared ${today} · From: Trent Brown, AmericanHospitalPrices.com*`);
    md.push("");
    md.push(`## Source data`);
    md.push("");
    md.push("- 9,539 hospitals with chargemaster data; 4,877 ranked publicly (50+ comparable procedures required).");
    md.push("- All prices sourced directly from each hospital's federally-mandated CMS Hospital Price Transparency disclosure.");
    md.push("- Live rankings: <https://americanhospitalprices.com/hospital-rankings>");
    md.push("- Methodology: <https://americanhospitalprices.com/data-methodology>");
    md.push("");
    if (c.description) {
      md.push(`## Campaign overview`);
      md.push("");
      md.push(c.description);
      md.push("");
    }
    if (c.notes) {
      md.push(`## Internal notes`);
      md.push("");
      md.push(c.notes);
      md.push("");
    }

    // Journalist pitch first (top of email)
    const pitches = [...byType("state_journalist_pitch"), ...byType("pitch_email"), ...byType("email_template")];
    if (pitches.length) {
      md.push(`## Journalist pitch`);
      md.push("");
      pitches.slice(0, 3).forEach((p, i) => {
        md.push(`### Pitch ${i + 1}`); md.push(""); md.push(p); md.push("");
      });
    }

    // Data brief (the headline statistic)
    const briefs = [...byType("data_brief")];
    if (briefs.length) {
      md.push(`## Headline statistics`);
      md.push("");
      briefs.forEach((b) => { md.push(b); md.push(""); });
    }

    // State / metro variance reports — useful as supporting data
    const variance = [...byType("state_variance_report"), ...byType("metro_variance_report")];
    if (variance.length) {
      md.push(`## Variance reports (${variance.length})`);
      md.push("");
      variance.slice(0, 5).forEach((v) => { md.push("---"); md.push(""); md.push(v); md.push(""); });
    }

    // Hospital spotlights — quotable specific examples
    const spotlights = [...byType("hospital_spotlight"), ...byType("system_spotlight"), ...byType("tx_deep_dive")];
    if (spotlights.length) {
      md.push(`## Hospital spotlights (${spotlights.length})`);
      md.push("");
      md.push("These are specific, quotable examples journalists can fact-check directly against the hospital's published chargemaster.");
      md.push("");
      spotlights.slice(0, 10).forEach((s) => { md.push("---"); md.push(""); md.push(s); md.push(""); });
    }

    md.push("");
    md.push(`---`);
    md.push("");
    md.push(`*Questions? Reply to this email or contact press@americanhospitalprices.com. Data is updated monthly from CMS-mandated disclosures.*`);

    const blob = new Blob([md.join("\n")], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `press-kit_${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}_${today}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const totals = useMemo(() => {
    let totalPosts = 0, totalPending = 0, totalApproved = 0, totalPosted = 0, totalTasks = 0;
    Object.values(postStats).forEach((s) => {
      totalPosts += s.total;
      totalPending += s.pending;
      totalApproved += s.approved;
      totalPosted += s.posted;
    });
    Object.values(taskStats).forEach((t) => { totalTasks += t.open_tasks; });
    return { totalPosts, totalPending, totalApproved, totalPosted, totalTasks };
  }, [postStats, taskStats]);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading campaigns…</div>;

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Active campaigns" value={campaigns.filter((c) => c.status === "active").length} sub={`${campaigns.length} total`} />
        <SummaryCard label="Total posts" value={totals.totalPosts} sub={`${totals.totalApproved} approved`} />
        <SummaryCard label="Pending approval" value={totals.totalPending} sub="needs review" highlight={totals.totalPending > 0} />
        <SummaryCard label="Posted live" value={totals.totalPosted} sub="across all platforms" />
        <SummaryCard label="Open tasks" value={totals.totalTasks} sub="across campaigns" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <span className="font-semibold">Filter:</span>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-2 py-1 border rounded bg-background">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="paused">Paused</option>
          <option value="done">Done</option>
          <option value="archived">Archived</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-2 py-1 border rounded bg-background">
          <option value="all">All types</option>
          <option value="data_study">Data study</option>
          <option value="outreach">Outreach</option>
          <option value="launch">Launch</option>
          <option value="content">Content</option>
          <option value="seo">SEO</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => void load()}>↻ Refresh</Button>
        <span className="text-muted-foreground">{filtered.length} of {campaigns.length} shown</span>
      </div>

      {/* Campaign table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-xs text-muted-foreground">
            <tr>
              <th className="text-left p-2 w-8"></th>
              <th className="text-left p-2">Campaign</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Geo</th>
              <th className="text-right p-2">Posts</th>
              <th className="text-right p-2">Pending</th>
              <th className="text-right p-2">Posted</th>
              <th className="text-right p-2">Tasks</th>
              <th className="text-left p-2">Platforms</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const ps = postStats[c.id];
              const ts = taskStats[c.id];
              const isOpen = expanded.has(c.id);
              return (
                <>
                  <tr
                    key={c.id}
                    className="border-t cursor-pointer hover:bg-muted/20"
                    onClick={() => toggle(c.id)}
                  >
                    <td className="p-2">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td className="p-2 font-medium">{c.name}</td>
                    <td className="p-2 flex items-center gap-1">
                      {c.campaign_type ? TYPE_ICON[c.campaign_type] : null}
                      <span className="text-xs">{c.campaign_type ?? "—"}</span>
                    </td>
                    <td className="p-2">
                      <Badge className={`${STATUS_COLOR[c.status] ?? "bg-gray-200"} border text-xs`}>{c.status}</Badge>
                    </td>
                    <td className="p-2 text-xs">{c.geo_scope?.join(", ") || "ALL"}</td>
                    <td className="p-2 text-right font-mono">{ps?.total ?? 0}</td>
                    <td className="p-2 text-right font-mono">
                      {ps?.pending ? <span className="text-orange-600">{ps.pending}</span> : 0}
                    </td>
                    <td className="p-2 text-right font-mono">
                      {ps?.posted ? <span className="text-green-600">{ps.posted}</span> : 0}
                    </td>
                    <td className="p-2 text-right font-mono">
                      {ts?.open_tasks ? <span className="text-blue-600">{ts.open_tasks}</span> : 0}
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">
                      {c.platforms?.join(", ") || "—"}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t bg-muted/10">
                      <td colSpan={10} className="p-3">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <div className="font-semibold text-xs mb-1">Description</div>
                            <div className="text-xs whitespace-pre-wrap">{c.description || "—"}</div>
                            {c.notes && (
                              <>
                                <div className="font-semibold text-xs mt-3 mb-1">Notes</div>
                                <div className="text-xs whitespace-pre-wrap">{c.notes}</div>
                              </>
                            )}
                            <div className="font-semibold text-xs mt-3 mb-1">Schedule</div>
                            <div className="text-xs">
                              {c.start_date ? `Start: ${c.start_date}` : "—"} {c.end_date ? `· End: ${c.end_date}` : ""}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); void downloadPressKit(c); }}
                                className="gap-1"
                              >
                                <Download size={12} /> Press kit (.md)
                              </Button>
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-xs mb-1">Content types in this campaign</div>
                            <div className="flex flex-wrap gap-1 mb-3">
                              {ps?.content_types.map((ct) => (
                                <Badge key={ct} variant="outline" className="text-[10px]">{ct}</Badge>
                              ))}
                              {!ps?.content_types.length && <span className="text-xs text-muted-foreground">No posts yet</span>}
                            </div>
                            <div className="font-semibold text-xs mb-1">Recent posts (top 5)</div>
                            <ul className="text-xs space-y-1">
                              {(recentPosts[c.id] || []).map((rp) => {
                                const url = externalLinkFor(rp.platform, rp.external_post_id);
                                return (
                                  <li key={rp.id} className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-[10px]">{rp.platform}</Badge>
                                    <span className="text-muted-foreground">{rp.content_type ?? "—"}</span>
                                    <span>· {rp.status}</span>
                                    {rp.state_code && <span>· {rp.state_code}</span>}
                                    {url && (
                                      <a href={url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-blue-600 hover:underline">
                                        <ExternalLink size={11} /> open
                                      </a>
                                    )}
                                  </li>
                                );
                              })}
                              {!recentPosts[c.id]?.length && <li className="text-muted-foreground">No posts yet</li>}
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No campaigns match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function SummaryCard({ label, value, sub, highlight }: { label: string; value: number; sub?: string; highlight?: boolean }) {
  return (
    <div className={`rounded border p-3 ${highlight ? "border-orange-400/60 bg-orange-50/40 dark:bg-orange-950/20" : "bg-card"}`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1 font-mono">{value.toLocaleString()}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default CampaignTracker;
